import { useEffect, type ReactElement } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';
import { useOfflinePanelUnclaimed } from './core/pwa/offline-presentation';
import { useReachState } from './core/reach/reach.store';
import { onNavigationSettled } from './core/pwa/update.store';
import { CLAIMS_OFFLINE_PANEL } from './core/pwa/works-offline';
import { OfflinePanel } from './shared/pwa/offline-panel';
import { ConnectionLine } from './shared/pwa/connection-line';
import { UpdateBanner } from './shared/pwa/update-banner';

/**
 * 021 FR-006/FR-007/FR-010/FR-022. The offline panel is mounted in TWO places, and the split is
 * the thing to understand rather than a duplication to tidy away.
 *
 * Here it covers every surface OUTSIDE the shell — `/sign-in` after an offline launch settles the
 * session down, and also `/password-reset` and `/verify-email`, which a signed-in practitioner can
 * reach from a mailbox link. Without this they would get a form that cannot possibly succeed and
 * says nothing about why, which is the opposite of FR-010.
 *
 * `layout.tsx` mounts it for the AUTHENTICATED surfaces, inside `main`, so that the toolbar, the
 * navigation and the person menu stay rendered and usable — which is what FR-022 requires and
 * what a single mount here cannot do: hiding the outlet from this level hides the chrome with it,
 * leaving a practitioner who cannot move around an application that claims to be presenting its
 * own interface. The e2e suite caught exactly that.
 *
 * The two are mutually exclusive by CLAIM rather than by inference (`core/pwa/offline-presentation.ts`),
 * so FR-007's "a single application-level indication" holds in every state: one panel is ever on
 * screen, never two, and never none.
 *
 * ---
 *
 * **034: this was `app.ts`, the component `bootstrapApplication` mounted. React Router has no
 * counterpart to a component wrapped around the router, so it is a PATHLESS ROOT ROUTE instead:
 * `app.routes.tsx` hangs every existing top-level entry under it as children.**
 *
 * That is not a filing choice. Two of the three things below need the router — the navigation
 * backstop needs to know a navigation finished, and the hidden outlet IS the router's output — and
 * a component rendered as a sibling of `RouterProvider` has neither. Being a route also means the
 * offline panel and the update banner reach the screen before any lazy chunk resolves, which is the
 * state they most need to work in.
 */
export default function AppRoot(): ReactElement {
  const location = useLocation();
  /**
   * FR-013's backstop, and the eager check that makes SC-008 true. A navigation is both the moment
   * a new version is worth looking for and the safest moment to apply one.
   *
   * Angular's store subscribed to `Router.events` and filtered for `NavigationEnd`. The effect
   * below is the same event: React Router commits a new `location` only once the navigation has
   * resolved, so a render carrying a new one IS a completed navigation. Keyed on `location.key`
   * rather than the pathname, because a repeated navigation to the same URL is still a navigation
   * and still a moment to check.
   */
  useEffect(() => {
    void onNavigationSettled();
  }, [location.key]);

  /**
   * Offline, and no shell has claimed the panel — which is true on every surface outside the
   * authenticated layout, including the two link-token routes a signed-in practitioner can reach.
   * Asking the claim rather than the session is what makes this correct on those.
   *
   * 036 (issue 219) adds a SECOND way a route can already be spoken for: `willClaim`, from the
   * matched route tree's own `handle` (`CLAIMS_OFFLINE_PANEL`), known synchronously from the URL —
   * before `layout.tsx`'s lazy module has even started loading, let alone mounted the effect that
   * actually claims it. Without this, an offline LAUNCH straight onto an authenticated route shows
   * this root's own panel for the whole window between the match and that effect firing, and the
   * shell's own copy (already rendering underneath, hidden by THIS panel's own `hidden={unclaimedHere}`
   * below) claims it a moment later — both exist in the DOM at once for that window, which is
   * `e2e-pwa/pwa.spec.ts`'s own "opens to its own interface" and "shows no task data" going red,
   * reproducibly, once an offline launch started reaching the shell at all instead of redirecting to
   * `/sign-in` first (a route this file never shared a claim race with, because the shell was never
   * on it).
   */
  /**
   * 036 (issue 208, research R-005): the same fact `layout.tsx` now reads for its own panel — what
   * has actually happened to a request, never `navigator.onLine` alone (FR-003).
   */
  const reach = useReachState();
  const offline = reach !== 'reachable' && reach !== 'unknown';
  const unclaimed = useOfflinePanelUnclaimed();
  const matches = useMatches();
  const willClaim = matches.some((match) => (match.handle as Record<string, unknown> | undefined)?.[CLAIMS_OFFLINE_PANEL] === true);
  /**
   * 036 (issue 208) — ONE definition, read by both the panel and the connection line below: a
   * surface no shell has claimed (or is about to). Splitting it into two identical expressions is
   * exactly how the two drifted apart once already — `layout.tsx` claims from a `useEffect`, which
   * commits AFTER the render its own JSX belongs to, so for that one render `unclaimed` alone was
   * still true here even on a route the shell was IN THE PROCESS of claiming. Missing `willClaim` on
   * the connection line (while the panel already had it, from issue 219) showed two
   * `connection-line`s at once on a handset-width run — `getByTestId` then a strict-mode violation,
   * not two lines the eye would ever have separated.
   */
  const unclaimedHere = offline && unclaimed && !willClaim;

  return (
    <>
      {/*
        Above everything, on every surface. The offer must reach an installed window whatever screen
        it happens to be on, and it must NOT be inside the element the offline panel hides — a client
        that is offline and out of date still has to be able to take the update when it reconnects.
      */}
      <UpdateBanner />
      {/*
        036 (issue 208, FR-005, contracts "The connection line"): under the SAME claim as the panel
        above — a surface the shell already claims draws its own copy (`layout.tsx`), so this one
        must not draw a second. Ordinary document flow, not fixed: there is no toolbar out here to
        reserve space beside, so showing or hiding it simply moves the form below it, which covers
        nothing (SC-022).
      */}
      {unclaimedHere && <ConnectionLine state={reach} />}
      {unclaimedHere && <OfflinePanel />}
      {/*
        HIDDEN rather than removed, exactly as the shell's outlet is. Unmounting the routed subtree
        would destroy whatever it holds and take an open editor's unsaved text with it (FR-024).
      */}
      <div hidden={unclaimedHere}><Outlet /></div>
    </>
  );
}
